/*
 * GSTR9CNew Common Actions
 *
 * Actions change things in your application
 * Since this boilerplate uses a uni-directional data flow, specifically redux,
 * we have these actions which are the only way your application interacts with
 * your application state. This guarantees that your state is up to date and nobody
 * messes it up weirdly somewhere.
 *
 * To add a new Action:
 * 1) Import your constant
 * 2) Add a function like this:
 *    export function yourAction(var) {
 *        return { type: YOUR_ACTION_CONSTANT, var: var }
 *    }
 */

import {
  LOADER_STATUS,
  PROFILE_INFO,
  ON_NOTIFY,
  RESPONSE_MESSAGE,
  SET_TABLE_DATA,
  TOTAL_RECORD,
  FILE_UPLOAD_STATUS,
  FILE_VALIDATION_ERROR,
  SET_ENTITY_LIST,
  SET_SELECTED_ENTITY,
  SET_SELECTED_GSTIN,
  SET_SELECTED_RETURN_PERIOD,
  SET_GSTR9C_DATA,
  SET_GSTR9C_HISTORY,
  SET_CLEAR_DATA,
} from "./constants";
import {
  getLocalStorage,
} from "components/Helper/Helper";
import { Config } from "../../../Config.js";
import axios from "axios";
import { toast } from "react-toastify";

/**
 * Changes the loader status
 *
 * @param  {boolean} status The loading status
 *
 * @return {object} An action object with a type of LOADER_STATUS
 */
export function loader(status) {
  return {
    type: LOADER_STATUS,
    loaderStatus: status,
  };
}

/**
 * Sets profile information
 *
 * @param  {object} payload The profile data
 *
 * @return {object} An action object with a type of PROFILE_INFO
 */
export function profileInfo(payload) {
  return {
    type: PROFILE_INFO,
    profileData: payload,
  };
}

/**
 * Shows notification message
 *
 * @param  {boolean} status The notification status
 * @param  {string} msg The notification message
 *
 * @return {object} An action object with a type of ON_NOTIFY
 */
export function onNotify(status, msg) {
  return {
    type: ON_NOTIFY,
    status: status,
    msg: msg,
  };
}

/**
 * Shows response message with error info
 *
 * @param  {boolean} status The response status
 * @param  {string} msg The response message
 * @param  {number} error The error code
 *
 * @return {object} An action object with a type of RESPONSE_MESSAGE
 */
export function responseMessage(status, msg, error) {
  return {
    type: RESPONSE_MESSAGE,
    msg: msg,
    status: status,
    error: error,
  };
}

/**
 * Sets table data
 *
 * @param  {array} data The table data
 *
 * @return {object} An action object with a type of SET_TABLE_DATA
 */
export function setTableData(data) {
  return {
    type: SET_TABLE_DATA,
    tableData: data,
  };
}

/**
 * Sets total record count
 *
 * @param  {number} val Total records count
 * @param  {array} recordVal Records array
 *
 * @return {object} An action object with a type of TOTAL_RECORD
 */
export function totalRecord(val, recordVal) {
  return {
    type: TOTAL_RECORD,
    totalRecords: val,
    records: recordVal,
  };
}

/**
 * Sets file upload status
 *
 * @param  {string} status The upload status
 *
 * @return {object} An action object with a type of FILE_UPLOAD_STATUS
 */
export function fileUploadStatus(status) {
  return {
    type: FILE_UPLOAD_STATUS,
    uploadStatus: status,
  };
}

/**
 * Sets file validation error
 *
 * @param  {string} error The validation error message
 *
 * @return {object} An action object with a type of FILE_VALIDATION_ERROR
 */
export function fileValidationError(error) {
  return {
    type: FILE_VALIDATION_ERROR,
    validationError: error,
  };
}

export function setEntityList(data) {
  return {
    type: SET_ENTITY_LIST,
    payload: data,
  }
};

/**
 * Sets selected entity
 *
 * @param  {string} entityId The selected entity ID
 *
 * @return {object} An action object with a type of SET_SELECTED_ENTITY
 */
export function setSelectedEntity(entityId) {
  return {
    type: SET_SELECTED_ENTITY,
    payload: entityId,
  };
}

/**
 * Sets selected GSTIN list
 *
 * @param  {array} gstinList The selected GSTIN list
 *
 * @return {object} An action object with a type of SET_SELECTED_GSTIN
 */
export function setSelectedGstin(gstinList) {
  return {
    type: SET_SELECTED_GSTIN,
    payload: gstinList,
  };
}

/**
 * Sets selected return period
 *
 * @param  {string} returnPeriod The selected return period
 *
 * @return {object} An action object with a type of SET_SELECTED_RETURN_PERIOD
 */
export function setSelectedReturnPeriod(returnPeriod) {
  return {
    type: SET_SELECTED_RETURN_PERIOD,
    payload: returnPeriod,
  };
}

/* 
 * Get profile information for the user
 * API call to fetch profile data including GSTIN list
 */
export const getProfile = (value) => {
  return (dispatch) => {
    dispatch(loader(true));
    let data = {
      profileId: value,
    };
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}gst/get_profile`,
        data: data,
        headers: {
          token: getLocalStorage("loginDetails"),
        },
      })
        .then((response) => {
          console.log("response ", response);
          if (response.data.error !== 1) {
            dispatch(profileInfo(response.data.profileInfo));
          }

          dispatch(loader(false));
          resolve(response.data.profileInfo || []);
        })
        .catch((error) => {
          dispatch(loader(false));
          console.log(error);
          reject(error);
        });
    });
  };
};

/*
 * Get GSTIN list by entity ID
 * Enhanced function to fetch GSTIN list based on selected entity
 */
export const getGstinByEntity = (entityId) => {
  return (dispatch) => {
    dispatch(loader(true));
    let data = {
      company_id: entityId,
    };
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}gst/get_profile`,
        data: data,
        headers: {
          token: getLocalStorage("loginDetails"),
        },
      })
        .then((response) => {
          console.log("GSTIN response ", response);
          if (response.data.error !== 1) {
            const gstinList = response.data.profileInfo || [];
            dispatch(profileInfo(gstinList));
            resolve(gstinList);
          } else {
            dispatch(profileInfo([]));
            resolve([]);
          }
          dispatch(loader(false));
        })
        .catch((error) => {
          dispatch(loader(false));
          dispatch(profileInfo([]));
          console.log(error);
          reject(error);
        });
    });
  };
};

/*
 * Generic file validation function
 * Validates file size, type, and other common validations
 */
export const validateFile = (file, maxSizeInMB = 5) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      // Check if file exists
      if (!file) {
        dispatch(fileValidationError("Please select a file"));
        reject("No file selected");
        return;
      }

      // Check file size
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        const errorMsg = `File size should not exceed ${maxSizeInMB}MB`;
        dispatch(fileValidationError(errorMsg));
        toast.error(errorMsg, {
          position: "bottom-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        reject(errorMsg);
        return;
      }

      // Check file type (Excel files)
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        const errorMsg = "Please select a valid Excel file (.xlsx, .xls, .csv)";
        dispatch(fileValidationError(errorMsg));
        toast.error(errorMsg, {
          position: "bottom-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        reject(errorMsg);
        return;
      }

      // File is valid
      dispatch(fileValidationError(""));
      resolve(true);
    });
  };
};

export function getEntityList(){
  return (dispatch) => {
    console.log('getEntityList action called');
    dispatch(loader(true));
    let data = {
      user_id:getLocalStorage("user_id").user_id
    };
    console.log('getEntityList data payload:', data);
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}console/getCompanyByUserId`,
        data,
        headers: {
          token: getLocalStorage("loginDetails"),
        },
      })
        .then((response) => {
          console.log('getEntityList response:', response);
          if (response.data.error !== 1) {
            let entityList = response.data.data.map((entity)=>{
              return {value:entity.company_id, label:entity.name}
            });
            console.log('Processed entityList:', entityList);
            dispatch(setEntityList(entityList));
            resolve(true);
          }else{
            console.log('getEntityList error response:', response.data);
            resolve([]);
          }
          dispatch(loader(false));
        })
        .catch((error) => {
          dispatch(loader(false));
          console.log('getEntityList error:', error);
          reject(true);
        });
    });
  }
};

/*
 * GSTR9C Specific Actions
 */

// Action creators for GSTR9C data
export function setGstr9CData(payload) {
  return {
    type: SET_GSTR9C_DATA,
    payload: payload,
  };
}

export function setGSTR9CHistoryData(payload) {
  return {
    type: SET_GSTR9C_HISTORY,
    payload: payload,
  };
}

// GSTR9C Submit Data Action
export const gstr9CSumbitData = (gstn, retPeriod) => {
  return (dispatch) => {
    let data = {
      gstn: gstn,
      ret_period: retPeriod,
    };
    dispatch(loader(true));
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}gstR9c/prepareGstr9cData`,
        data: data,
        headers: {
          token: getLocalStorage("loginDetails"),
        },
      })
        .then((response) => {
          if (response.data.error == "0") {
            let gross_turnover = [];
            let basic_details = {};
            let table5 = {};
            let table6 = [];
            let table7 = [];
            let table8 = [];
            let table9 = {};
            let table10 = [];
            let table11 = {};
            let table12 = {};
            let table13 = [];
            let table14 = {};
            let table15 = [];
            let table16 = [];
            let table17 = [];

            const fullData = response.data.gstr9cdata.audited_data;
            basic_details.osup_det = {
              detail: `${
                Number(fullData.fp.slice(2)) - 1
              } - ${fullData.fp.slice(2)}`,
            };
            basic_details.osup_zero = { detail: fullData.gstin };
            basic_details.osup_nil_exmp = { detail: fullData.lgl_name||"" };
            basic_details.isup_rev = { detail: fullData.trd_name||""  };
            basic_details.isup_act =  { detail: fullData.act_name||"" };
            table5 = fullData.table5;
            table6 = fullData.table6?.rsn;

            table7.push(fullData.table7);
            table8 = fullData.table8?.rsn;
            table9 = fullData.table9;
            table10 = fullData.table10?.rsn;
            table11 = fullData.table11;
            table12 = fullData.table12;
            table13 = fullData.table13?.rsn;
            table14 = fullData.table14;
            table15 = fullData.table15?.rsn;
            table16.push(fullData.table16);
            table17 = fullData.add_liab?.tax_pay;
            
            dispatch(
              setGstr9CData({
                basic_details,
                table5,
                table6,
                table7,
                table8,
                table9,
                table10,
                table11,
                table12,
                table13,
                table14,
                table15,
                table16,
                table17,
              })
            );
          } else {
            console.log("responceData", response.data);
            toast.error(response.data.msg, {
              position: "bottom-center",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          }
          dispatch(loader(false));
          resolve(true);
        })
        .catch((error) => {
          dispatch(loader(false));
          console.log(error);
          reject(true);
        });
    });
  };
};

// Auto Fetch GSTR9C Data Action
export const autoFetchgstr9CData = (gstn, retPeriod) => {
  return (dispatch) => {
    let data = {
      gstn: gstn,
      ret_period: retPeriod,
    };
    dispatch(loader(true));
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}gstR9c/getGstr9cRecords`,
        data: data,
        headers: {
          token: getLocalStorage("loginDetails"),
        },
      })
        .then((response) => {
          if (response.data.error == "0") {
            let gross_turnover = [];
            let basic_details = {};
            let table5 = {};
            let table6 = [];
            let table7 = [];
            let table8 = [];
            let table9 = {};
            let table10 = [];
            let table11 = {};
            let table12 = {};
            let table13 = [];
            let table14 = {};
            let table15 = [];
            let table16 = [];
            let table17 = [];

            const fullData = response.data.gstr9cdata.audited_data;
            basic_details.osup_det = {
              detail: `${
                Number(fullData.fp.slice(2)) - 1
              } - ${fullData.fp.slice(2)}`,
            };
            basic_details.osup_zero = { detail: fullData.gstin };
            basic_details.osup_nil_exmp = { detail: '' };
            basic_details.isup_rev =  { detail: '' };
            basic_details.isup_act = { detail: '' };
            table5 = fullData.table5;
            table6 = fullData.table6?.rsn;

            table7.push(fullData.table7);
            table8 = fullData.table8?.rsn;
            table9 = fullData.table9;
            table10 = fullData.table10?.rsn;
            table11 = fullData.table11;
            table12 = fullData.table12;
            table13 = fullData.table13?.rsn;
            table14 = fullData.table14;
            table15 = fullData.table15?.rsn;
            table16.push(fullData.table16);
            table17 = fullData.add_liab?.tax_pay;
            
            dispatch(
              setGstr9CData({
                basic_details,
                table5,
                table6,
                table7,
                table8,
                table9,
                table10,
                table11,
                table12,
                table13,
                table14,
                table15,
                table16,
                table17,
              })
            );
            
            toast.success("Data fetched successfully", {
              position: "bottom-center",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          } else {
            toast.error(response.data.msg, {
              position: "bottom-center",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          }
          dispatch(loader(false));
          resolve(true);
        })
        .catch((error) => {
          dispatch(loader(false));
          console.log(error);
          reject(true);
        });
    });
  };
};

// Download Form Action
export const downloadForm = (gstn, retPeriod) => {
  return (dispatch) => {
    let data = {
      gstn: gstn,
      ret_period: retPeriod,
    };
    dispatch(loader(true));
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}gstR9c/downloadGstr9cReport`,
        data: data,
        headers: {
          token: getLocalStorage("loginDetails"),
        },
        responseType: "blob",
      })
        .then((response) => {
          console.log(response);
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "GSTR_9C_Download_Report.xlsx");
          document.body.appendChild(link);
          link.click();
          dispatch(loader(false));
          resolve(true);
        })
        .catch((error) => {
          dispatch(loader(false));
          reject(true);
        });
    });
  };
};

// Upload File Action
export const onCallUpload = (data) => {
  return (dispatch) => {
    dispatch(loader(true));
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}gstR9c/uploadGstr9cFile`,
        data: data,
        timeout: 600000,
        headers: {
          token: getLocalStorage("loginDetails"),
          "Content-Type": "multipart/form-data",
        },
      })
        .then((response) => {
          if (response.data.error != 1) {
            toast.success(response.data.msg, {
              position: "bottom-center",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            resolve(true);
          } else {
            toast.error(`${response.data.msg}`, {
              position: "bottom-center",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            resolve(false);
          }
          dispatch(loader(false));
        })
        .catch((error) => {
          console.log(error);
          dispatch(loader(false));
          reject(true);
        });
    });
  };
};

// Get GSTR9C Data History Action
export const getGSTR9CDataHistory = (type) => {
  return (dispatch) => {
    let data = {
      fetch_type: type,
    };
    dispatch(loader(true));
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}gstR9c/getGstr9cHistoryData`,
        data: data,
        headers: {
          token: getLocalStorage("loginDetails"),
        },
      })
        .then((response) => {
          if (response.data.error != 1) {
            let data = response.data.data;
            let modifiedData = data.map((item) => {
              let returnItem = { ...item };
              returnItem.created_date = new Date(item.created_date);
              return returnItem;
            });
            dispatch(setGSTR9CHistoryData(modifiedData));
          }
          dispatch(loader(false));
          resolve(true);
        })
        .catch((error) => {
          dispatch(loader(false));
          console.log(error);
          reject(true);
        });
    });
  };
};

// Download Auto Populated History Action
export const downloadAutoPopulatedHistory = (fileId, type) => {
  return (dispatch) => {
    let value = {
      file_id: fileId,
      fetch_type: type,
    };
    dispatch(loader(true));
    return new Promise((resolve, reject) => {
      axios({
        method: "post",
        url: `${Config.url}gstR9c/downloadGstr9cHistoryReport`,
        headers: {
          token: getLocalStorage("loginDetails"),
        },
        data: value,
        responseType: "arraybuffer",
      })
        .then((response) => {
          console.log("Response", response);
          const url = URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${fileId}_GSTR9C.xlsx`);
          document.body.appendChild(link);
          link.click();
          dispatch(loader(false));
          resolve(true);
        })
        .catch((error) => {
          dispatch(loader(false));
          console.log(error);
          reject(true);
        });
    });
  };
};
